import React, { useMemo, useState } from 'react';

import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';

import {
  linkedRequirementIds,
  notApprovableRankIds,
} from '@modules/advancement/constants';
import {
  GroupedPendingItem,
  GroupedPendingItemChild,
  PendingItem,
  TableClickActions,
} from '@modules/advancement/pendingItems/types';
import { searchOnItems } from '@modules/advancement/purchaseOrder/utils';
import {
  ItemsPerSubUnit,
  ToApproveItems,
} from '@modules/advancement/unitAdvancement/types';
import { ApproveAdvancementsButton } from '@modules/progress/approveAdvancements';
import {
  programTypeName,
  ranksRequirementIdsWithoutApproveOption,
} from '@modules/shared';
import { PaginationTotal, Table } from '@shared/components';

import styles from './PendingItemsTable.less';
import { groupedColumns } from './PendingRequirementsColumns';

type SelectedItemKey = string | number;

interface PendingRequirementsTableNotApprovable {
  onGoToYouthProfile: TableClickActions['onGoToYouthProfile'];
  onGoToAdultProfile: TableClickActions['onGoToAdultProfile'];
  searchQuery: string;
  notApprovableItemsWithChildren: GroupedPendingItem[];
  isLoading: boolean;
}

const PendingRequirementsTableNotApprovable: React.FC<
  PendingRequirementsTableNotApprovable
> = props => {
  const {
    searchQuery,
    notApprovableItemsWithChildren: dataSource,
    isLoading,
    onGoToYouthProfile,
    onGoToAdultProfile,
  } = props;

  const [selectedRowKeys, setSelectedRowKeys] = useState<SelectedItemKey[]>([]);
  const searchedItemsNotApprovable = searchOnItems(searchQuery, dataSource);
  const areAnyAdvancementsSelected = selectedRowKeys.length > 0;
  const selectedAdvancements = useMemo(() => {
    const setKeys = new Set(selectedRowKeys);

    return dataSource
      .flatMap(({ children }) => children)
      .filter(item => setKeys.has(item.key))
      .filter(Boolean);
  }, [dataSource, selectedRowKeys]);

  const isTherePack = (items: ToApproveItems[]) =>
    (items as ItemsPerSubUnit[]).some(sub =>
      sub.children.some(item => item.program === programTypeName.CUB_SCOUT),
    );

  const isTherePackAdvancement = isTherePack(dataSource);

  const renderTitle = () => (
    <React.Fragment>
      <ApproveAdvancementsButton
        className={styles.approveAdvancements}
        disabled={!areAnyAdvancementsSelected}
        personsAdvancements={selectedAdvancements}
        advancementStatus={'record'}
      >
        {isTherePackAdvancement && (
          <FormattedMessage
            id="progress.RecordAdvancementsButton.title"
            values={{
              count: selectedAdvancements.length,
            }}
          />
        )}
      </ApproveAdvancementsButton>
    </React.Fragment>
  );

  const isLinkedRequirements = (parentRequirementId: number | undefined) => {
    if (
      parentRequirementId !== undefined &&
      linkedRequirementIds.includes(parentRequirementId)
    ) {
      return true;
    }

    return false;
  };

  const isNotApprovableChildren = (record: GroupedPendingItemChild) => {
    if (isLinkedRequirements(record.parentRequirementId)) {
      return true;
    }

    if (
      record.program !== undefined &&
      notApprovableRankIds.includes(record.id)
    ) {
      return true;
    }

    if (ranksRequirementIdsWithoutApproveOption.includes(record.id)) {
      return true;
    }

    return false;
  };

  const isNotApprovable = (record: GroupedPendingItem | PendingItem) => {
    const parentRequirementId = (record as PendingItem).parentRequirementId;
    const program = (record as PendingItem).program;
    const totalAmount = (record as GroupedPendingItem).totalAmount;

    if (
      program !== undefined &&
      (program !== programTypeName.CUB_SCOUT ||
        notApprovableRankIds.includes((record as PendingItem).id))
    ) {
      return true;
    }

    if (isLinkedRequirements(parentRequirementId)) {
      return true;
    }

    if (totalAmount !== undefined && totalAmount === 0) {
      return true;
    }

    if (
      ranksRequirementIdsWithoutApproveOption.includes(
        (record as PendingItem).id,
      )
    ) {
      return true;
    }

    return false;
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    getCheckboxProps: (record: GroupedPendingItem | PendingItem) => ({
      disabled: isNotApprovable(record),
      style: isNotApprovable(record) ? { display: 'none' } : {},
    }),
    onSelect(record: GroupedPendingItem | PendingItem, selected: boolean) {
      const isGroup = 'groupId' in record;
      if (selected) {
        if (isGroup) {
          const selected = record.children
            .filter(item => !isNotApprovableChildren(item))
            .map(item => item.key);
          setSelectedRowKeys([...selectedRowKeys, ...selected, record.key]);
        } else {
          setSelectedRowKeys([...selectedRowKeys, record.key]);
        }
      } else if (isGroup) {
        const selectedItems = new Set<string | number>(selectedRowKeys);
        selectedItems.delete(record.key);
        record.children
          .filter(item => !isNotApprovableChildren(item))
          .forEach(item => {
            selectedItems.delete(item.key);
          });
        setSelectedRowKeys(Array.from(selectedItems));
      } else {
        const currentSelectedKeys = new Set<string | number>(selectedRowKeys);
        currentSelectedKeys.delete(record.key);
        setSelectedRowKeys(Array.from(currentSelectedKeys));
      }
    },
    columnTitle: <></>,
  };

  return (
    <div className={styles.notApprovableContainer}>
      <p className={styles.disclaimer}>
        <FormattedMessage id="advancement.PendingItems.nonApprovable.disclaimer" />
      </p>
      <Table
        dataSource={searchedItemsNotApprovable}
        loading={isLoading}
        className={styles.table}
        title={renderTitle}
        columns={groupedColumns({
          onGoToYouthProfile,
          onGoToAdultProfile,
        })}
        rowSelection={rowSelection}
        pagination={{
          showTotal: () => {
            const totalSelected = selectedRowKeys.filter(
              (item: string) => !item?.includes('group'),
            ).length;
            const totalNoGroup = dataSource.flatMap(
              item => item.children,
            )?.length;

            return (
              <PaginationTotal total={totalNoGroup} selected={totalSelected} />
            );
          },
        }}
        rowClassName={(row: GroupedPendingItem) => {
          if (isEmpty(row?.children)) return styles.rowItem;
          else return styles.rowItemGroup;
        }}
        size="small"
        scroll={{ x: true }}
      />
    </div>
  );
};

export default PendingRequirementsTableNotApprovable;
